import type {
  ApiResponse,
  Credential,
  ProductConfig,
  DonationsConfig,
  SiteConfig,
  CheckoutRequest,
  CheckoutResponse,
  CheckoutCompletionResponse,
  TrialStartRequest,
  TrialStartResponse,
  TrialFinalizeRequest,
  PromoValidationRequest,
  PromoValidationResponse,
  AccessResponse,
  MagicLinkRequest,
  VerifyResponse,
  EstablishSessionResponse,
  Purchase,
  FunnelSurface,
  FunnelFlow,
} from '@/types';
import { getToken, tokenVerified, keepsVerifiedSession, sameSessionVisitor, clearSession, beginSessionRequest, type SessionRequest, shouldSendHeartbeat, isSandboxMode, getSandboxGateToken, setSandboxGateToken, clearSandboxGateToken, clearSandboxMode, clearAdRemovalCookie, setPendingCheckout, clearPendingCheckout, setTestKeysBackend } from '@/session';
import { setPendingUpgrade, clearPendingUpgrade } from '@/session';
import type { Stripe, StripeError } from '@stripe/stripe-js';
import { logError, logWarn } from '@/sdkConsole';
import { isKnownCrawler } from '@/crawler';
import { on as onAccessChange } from '@/accessEvents';
import { applyVisitorLanguage } from '@/i18n/loader';

let apiBase: string | null = null;

// getChannelSlug returns the Open.Video channel slug when the widget runs on a
// portal watch page (open.video/@slug). The backend uses it to resolve the
// channel's owner domain as the tenant instead of the portal host. Empty
// everywhere else: publisher sites and CNAME hosts resolve by host alone, and
// {slug}.open.video subdomains are parsed server-side from the hostname.
// Slugs have no charset contract — active channels include leading
// underscores/hyphens and mixed case (__open-video-account-*, -black-stars),
// so take the whole path segment and let the server validate. Lowercasing is
// safe: HumixChannel.Slug collates utf8mb3_unicode_ci (case-insensitive).
function getChannelSlug(): string {
  const host = window.location.hostname.toLowerCase();
  if (host !== 'open.video' && host !== 'www.open.video') return '';
  const match = /^\/@([^/]+)/.exec(window.location.pathname.toLowerCase());
  return match?.[1] ?? '';
}

// withSlug appends the channel-slug hint to a tenant-scoped request URL. It is
// a no-op off the portal, so publisher-site traffic is byte-identical to before.
function withSlug(url: string): string {
  const slug = getChannelSlug();
  if (!slug) return url;
  return `${url}${url.includes('?') ? '&' : '?'}slug=${encodeURIComponent(slug)}`;
}

// ApiError carries the HTTP status of a failed onsite call so callers can
// route on it (e.g. 401 = the identity credential is dead — re-identify)
// instead of parsing message text.
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number, public code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// isSessionExpired reports whether a checkout-start failure is the backend's
// session-expired signal (ErrSessionExpired, 401): the credential the visitor
// presented no longer resolves, so they must re-identify.
export function isSessionExpired(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

// isRequestRejected reports whether the backend definitively refused a request
// (a 4xx), as opposed to a transport failure or 5xx where a retry may succeed.
// An expired session (401), a timeout (408) and rate limiting (429) are
// retryable, not refusals.
export function isRequestRejected(err: unknown): boolean {
  if (!(err instanceof ApiError)) {
    return false;
  }
  if (err.status === 401 || err.status === 408 || err.status === 429) {
    return false;
  }
  return err.status >= 400 && err.status < 500;
}

// isDomainNotFound reports whether a checkout-start failure is the backend's
// unresolvable-tenant signal (400 "Domain not recognized"). On a local origin
// the flows route this to the local-dev guidance instead of the generic error
// step — the same gate the config fetchers apply via FetchResult.
export function isDomainNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 400 && err.message === 'Domain not recognized';
}

// authHeaders bears the visitor session when one exists, plus the preview
// token in sandbox mode (previewHeaders).
function authHeaders(): Record<string, string> {
  const token = getToken();
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...previewHeaders() };
}

// Mirrors visitorauth.PreviewHeader / PreviewExpiresHeader.
const PREVIEW_HEADER = 'X-Ezsm-Preview';
const PREVIEW_EXPIRES_HEADER = 'X-Ezsm-Preview-Expires';

// previewHeaders carries the sandbox preview token in its own header, never as
// the bearer: the sandbox gate accepts only it, and the bearer stays the
// visitor's identity. Empty on live, so live requests are unchanged.
function previewHeaders(): Record<string, string> {
  const preview = isSandboxMode() ? getSandboxGateToken() : null;
  return preview ? { [PREVIEW_HEADER]: preview } : {};
}

// inFlightKey shares a pending request only between callers sending the same
// credentials: a request answers for the bearer and preview token it carried.
function inFlightKey(url: string, headers: Record<string, string>): string {
  return `${url}\n${headers.Authorization ?? ''}\n${headers[PREVIEW_HEADER] ?? ''}`;
}

// sandboxHeaders carries the session token on calls that are unauthenticated on
// live but gated on the sandbox instance (every onsite route there requires the
// sandbox session). On live they stay header-free so anonymous traffic keeps
// its preflight-less simple requests.
function sandboxHeaders(): Record<string, string> {
  return isSandboxMode() ? authHeaders() : {};
}

// Sliding-refresh response headers (mirrors visitorauth.SessionRefresh*). The
// backend attaches a freshly-minted session token to an authenticated
// /onsite/me/* response when the current one is near expiry, so an active reader
// is never logged out.
const SESSION_REFRESH_HEADER = 'X-Ezsm-Session';
const SESSION_REFRESH_EXPIRES_HEADER = 'X-Ezsm-Session-Expires';

// The sandbox gate's two 401 bodies (sandboxauth.RequireSandboxSession).
// Matched to distinguish a true gate rejection — the preview credential itself
// is dead — from an application 401 a gated route can also return (e.g.
// checkout's ErrSessionExpired for a dead ezAuth credential), which the
// caller's re-identify flow handles and must not end the preview.
const GATE_REJECTION_MESSAGES = new Set(['Sandbox session required', 'Invalid or expired sandbox session']);

// exitSandboxOnGateRejection ends the preview when the sandbox gate itself
// rejects the credential: it is dead (expired mid-preview, signing-key
// rotation) and no client-side recovery exists — staying up would strand the
// publisher on a broken page with only the banner's Exit button as a way out.
// Clears sandbox state and reloads onto live. Callers pass the parsed response
// message; an unreadable or unmatched body is treated as an application error
// (no exit — conservative). NOT for /me routes: an anonymous previewer 401s
// those by design (handled by the caller).
function exitSandboxOnGateRejection(resp: Response, message: string | undefined): void {
  if (!isSandboxMode() || resp.status !== 401 || !GATE_REJECTION_MESSAGES.has(message ?? '')) return;
  clearSession();
  clearSandboxGateToken();
  clearSandboxMode();
  clearAdRemovalCookie();
  logError('sandbox gate rejected the preview credential; exiting sandbox mode');
  window.location.reload();
}

// fetchSession performs a request under a SessionRequest begun at send time and
// hands any sliding session refresh to it (SessionRequest.renew routes it by
// the bearer this fetch sent). An operation-scoped caller passes its operation
// so a later adopt is judged from the operation's start. Live responses carry
// no refresh header, so renewal is a no-op there.
async function fetchSession(input: string, init: RequestInit, operation?: SessionRequest): Promise<{ resp: Response; session: SessionRequest }> {
  const session = beginSessionRequest(operation);
  const resp = await fetch(input, init);
  const jwt = resp.headers.get(SESSION_REFRESH_HEADER);
  if (jwt) {
    session.renew(jwt, resp.headers.get(SESSION_REFRESH_EXPIRES_HEADER) ?? '');
  }
  // The sandbox gate's preview refresh; a preview that exited meanwhile stays exited.
  const preview = resp.headers.get(PREVIEW_HEADER);
  if (preview && isSandboxMode()) {
    setSandboxGateToken(preview, resp.headers.get(PREVIEW_EXPIRES_HEADER) ?? '');
  }
  return { resp, session };
}

async function fetchWithRefresh(input: string, init: RequestInit): Promise<Response> {
  return (await fetchSession(input, init)).resp;
}

// Production API host, baked in at build time. Publishers embed just
//   <script src="https://sm.ezoic.com/min.js" async defer></script>
// with no extra attributes. `data-api` on the script tag is an OPTIONAL
// override used for local dev / staging (see widget/index.html), and takes
// precedence when present.
//
// Keep every Ezoic host here absolute; never derive one from the script's own
// URL (`document.currentScript.src`, `import.meta.url`) — publisher page
// optimizers re-execute min.js from a `blob:` URL. See
// docs/architecture/widget-embed.md.
const DEFAULT_API_BASE = 'https://smbe.ezoic.com';

// Sandbox preview host, baked in at build time (mirrors DEFAULT_API_BASE). In
// local dev the single instance runs in sandbox mode, so a data-api override
// doubles as the sandbox base there.
const DEFAULT_SANDBOX_API_BASE = 'https://subscriptions-manager.sandbox.ezoic.com';

function getApiBase(): string {
  if (apiBase !== null) return apiBase;

  const override = document
    .querySelector<HTMLScriptElement>('script[data-api]')
    ?.getAttribute('data-api');
  apiBase = override ?? DEFAULT_API_BASE;
  return apiBase;
}

// getSandboxApiBase returns the sandbox instance's origin. The handoff exchange
// targets it explicitly (apiBase still points at live until the exchange
// succeeds), and activateSandbox re-points apiBase here once it has.
export function getSandboxApiBase(): string {
  const override = document
    .querySelector<HTMLScriptElement>('script[data-api]')
    ?.getAttribute('data-api');
  return override ?? DEFAULT_SANDBOX_API_BASE;
}

// setApiBase re-points every subsequent API call at the given origin. Called by
// sandbox activation at boot, before any fetch — so the memo is set, not
// mutated mid-flight. The config memos and the test-keys bit are dropped too:
// a different instance can answer differently (the sandbox bit, a mirrored
// catalog).
export function setApiBase(base: string): void {
  apiBase = base;
  siteConfigPromise = null;
  productConfigInFlight.clear();
  accessInFlight.clear();
  priceConfigPromises.clear();
  setTestKeysBackend(false);
}

// resetApiBase clears the memo so the next call re-derives the live base from
// the data-api override / built-in default. Used to leave sandbox mode after a
// failed sandbox-token exchange.
export function resetApiBase(): void {
  apiBase = null;
  siteConfigPromise = null;
  productConfigInFlight.clear();
  accessInFlight.clear();
  priceConfigPromises.clear();
  setTestKeysBackend(false);
}

// Production subscriber-portal host, baked in at build time (mirrors
// DEFAULT_API_BASE). The portal is where a visitor signs in to manage payment
// methods, view receipts, and cancel subscriptions. `data-portal` on the script
// tag is an OPTIONAL override for local dev / staging.
const DEFAULT_PORTAL_BASE = 'https://subscriber.ezoic.com';

// Sandbox subscriber-portal host (mirrors DEFAULT_SANDBOX_API_BASE). A sandbox
// session must stay on the sandbox portal: the live portal reads Livemode=1 and
// shows none of the sandbox purchase.
const DEFAULT_SANDBOX_PORTAL_BASE = 'https://subscriber.sandbox.ezoic.com';

// Open Video subscriber-portal host. When the widget runs on an OV surface
// (open.video or a {slug}.open.video channel subdomain), "manage subscription"
// opens the OV-branded portal so the subscriber stays in OV land. Custom CNAME
// channel domains can't be detected client-side and keep the default portal.
const DEFAULT_OV_PORTAL_BASE = 'https://subscriber.open.video';

// Sandbox twin of DEFAULT_OV_PORTAL_BASE: a sandbox session on an OV surface
// stays in OV branding AND on the sandbox environment — the live OV portal
// reads Livemode=1 and rejects sandbox-signed tokens.
const DEFAULT_OV_SANDBOX_PORTAL_BASE = 'https://subscriber.sandbox.open.video';

// Intentionally broader than the server's domainresolver.IsOpenVideoHost
// (single-label only): the server extracts a channel slug from the host, while
// here the question is just "is this page OV-operated" — and every
// *.open.video host is.
function isOpenVideoHostPage(): boolean {
  const host = window.location.hostname.toLowerCase();
  return host === 'open.video' || host.endsWith('.open.video');
}

// getPortalBase returns the subscriber-portal origin. Unlike getApiBase it is
// not memoized: it is read only on render/click (not per request), so a fresh
// DOM lookup is negligible and keeps the override testable.
export function getPortalBase(): string {
  const override = document
    .querySelector<HTMLScriptElement>('script[data-portal]')
    ?.getAttribute('data-portal');
  if (override) {
    return override;
  }
  if (isSandboxMode()) {
    if (isOpenVideoHostPage()) {
      return DEFAULT_OV_SANDBOX_PORTAL_BASE;
    }
    return DEFAULT_SANDBOX_PORTAL_BASE;
  }
  if (isOpenVideoHostPage()) {
    return DEFAULT_OV_PORTAL_BASE;
  }
  return DEFAULT_PORTAL_BASE;
}

// fetchProductConfig loads the product a publisher-authored product handle
// resolves to so showPaywall({ product }) can render its active prices.
// promoCode is the mount's campaign code (URL stash or the showPaywall
// promoCode option): the response's promo block carries the plan picker's
// per-price discounts for it.
// FetchResult carries the failure kind the local-dev guidance gates on.
// domainNotFound is true only when the backend answered a tenant 404 — either
// "Domain not recognized" (unresolvable tenant) or "Domain not set up for
// Subscriptions" (resolvable in Middleton but never onboarded; see
// errDomainNotSetup in pkg/onsite). Both mean this tenant can never serve SM.
// Network failures, 5xx, and unknown product/price handles on a valid domain
// all leave it false, so a down or unreachable backend never reads as "no
// such site". domainNotSetup narrows the tenant 404 to the never-onboarded
// case so the guidance copy can name the real fix (finish setup) instead of
// the alias advice. domainDisabled is the 403 "Subscriptions are not enabled
// for this domain" — the same permanent tenant state, but a deliberate
// switch-off rather than a resolution failure, so it never routes to the
// local-dev guidance (the domain resolved fine; it is just off).
export interface FetchResult<T> {
  config: T | null;
  domainNotFound: boolean;
  domainNotSetup: boolean;
  domainDisabled: boolean;
}

function configFailure(resp: Response, body: ApiResponse<unknown> | null): FetchResult<never> {
  const domainNotSetup = resp.status === 404 && body?.message === 'Domain not set up for Subscriptions';
  const domainNotFound = domainNotSetup || (resp.status === 404 && body?.message === 'Domain not recognized');
  const domainDisabled = resp.status === 403 && body?.message === 'Subscriptions are not enabled for this domain';
  return { config: null, domainNotFound, domainNotSetup, domainDisabled };
}

export async function fetchProductConfig(product: string, promoCode?: string): Promise<ProductConfig | null> {
  return (await fetchProductConfigDetailed(product, promoCode)).config;
}

// Share pending loads only; settled results must not delay catalog edits.
const productConfigInFlight = new Map<string, Promise<FetchResult<ProductConfig>>>();

export function fetchProductConfigDetailed(product: string, promoCode?: string): Promise<FetchResult<ProductConfig>> {
  const origin = window.location.origin;
  const base = getApiBase();
  let url = withSlug(`${base}/onsite/product-config?origin=${encodeURIComponent(origin)}&product=${encodeURIComponent(product)}`);
  if (promoCode) {
    url += `&promo=${encodeURIComponent(promoCode)}`;
  }
  const headers = sandboxHeaders();
  const key = inFlightKey(url, headers);
  const pending = productConfigInFlight.get(key);
  if (pending) {
    return pending;
  }
  const promise = loadProductConfig(url, headers, product).finally(() => {
    // A pre-reset load must not evict a newer request with the same key.
    if (productConfigInFlight.get(key) === promise) {
      productConfigInFlight.delete(key);
    }
  });
  productConfigInFlight.set(key, promise);
  return promise;
}

async function loadProductConfig(url: string, headers: Record<string, string>, product: string): Promise<FetchResult<ProductConfig>> {
  try {
    // fetchWithRefresh adopts the gate token's sliding refresh here: the
    // anonymous previewer never reaches /me (sdk short-circuits on
    // !hasSession()), so the gated GETs are where it happens.
    const resp = await fetchWithRefresh(url, { headers });
    if (!resp.ok) {
      const body = await resp.json().catch(() => null) as ApiResponse<unknown> | null;
      logError('product-config request failed', resp.status, product);
      exitSandboxOnGateRejection(resp, body?.message);
      return configFailure(resp, body);
    }
    const body: ApiResponse<ProductConfig> = await resp.json();
    if (!body.success) return { config: null, domainNotFound: false, domainNotSetup: false, domainDisabled: false };
    // A productless success (unknown handle, invalid key, or no active prices)
    // is a resolved domain with nothing to sell. Pass the domain config through
    // so the caller's `!config.product` branch runs — one console error naming
    // the handle, no modal. Null config stays reserved for load failures.
    applyVisitorLanguage(body.data.visitorLanguage);
    return { config: body.data, domainNotFound: false, domainNotSetup: false, domainDisabled: false };
  } catch (err) {
    logError('product-config request errored', err);
    return { config: null, domainNotFound: false, domainNotSetup: false, domainDisabled: false };
  }
}

// Memoized price-config loads, keyed by price handle AND campaign code:
// openCheckout awaits the config to theme the host and preselect the price
// before mount. The promo code is part of the key because the response's promo
// block is per-code. A failed load is not cached (mirrors getSiteConfig) so a
// later call retries.
const priceConfigPromises = new Map<string, Promise<FetchResult<ProductConfig>>>();

// fetchPriceConfig loads the active price (and its parent product) a
// publisher-authored price handle resolves to, so openCheckout({ price }) can
// theme the host and label the checkout gate with the catalog price and
// product name instead of a placeholder. Same wire shape as product-config.
// promoCode: see fetchProductConfig. Returns null on any failure — the caller
// falls back to the default theme / generic summary and never blocks on this
// fetch.
export async function fetchPriceConfig(price: string, promoCode?: string): Promise<ProductConfig | null> {
  return (await fetchPriceConfigDetailed(price, promoCode)).config;
}

export async function fetchPriceConfigDetailed(price: string, promoCode?: string): Promise<FetchResult<ProductConfig>> {
  const key = `${price}\n${promoCode ?? ''}`;
  const cached = priceConfigPromises.get(key);
  if (cached) {
    return cached;
  }
  const promise = loadPriceConfig(price, promoCode).then((result) => {
    if (!result.config) {
      priceConfigPromises.delete(key);
    }
    return result;
  });
  priceConfigPromises.set(key, promise);
  return promise;
}

async function loadPriceConfig(price: string, promoCode?: string): Promise<FetchResult<ProductConfig>> {
  const origin = window.location.origin;
  const base = getApiBase();
  let url = withSlug(`${base}/onsite/price-config?origin=${encodeURIComponent(origin)}&price=${encodeURIComponent(price)}`);
  if (promoCode) {
    url += `&promo=${encodeURIComponent(promoCode)}`;
  }

  try {
    // fetchWithRefresh adopts the gate token's sliding refresh on these gated
    // GETs (see fetchProductConfig).
    const resp = await fetchWithRefresh(url, { headers: sandboxHeaders() });
    if (!resp.ok) {
      const body = await resp.json().catch(() => null) as ApiResponse<unknown> | null;
      logError('price-config request failed', resp.status, price);
      exitSandboxOnGateRejection(resp, body?.message);
      return configFailure(resp, body);
    }
    const body: ApiResponse<ProductConfig> = await resp.json();
    if (!body.success) return { config: null, domainNotFound: false, domainNotSetup: false, domainDisabled: false };
    // Same productless-success handling as fetchProductConfigDetailed: an
    // unknown price key on a valid domain is a resolved domain with nothing
    // to sell, not a load failure. Pass the domain config through so the
    // caller's `!priceConfig.product` branch runs — one console error naming
    // the price, no modal.
    applyVisitorLanguage(body.data.visitorLanguage);
    return { config: body.data, domainNotFound: false, domainNotSetup: false, domainDisabled: false };
  } catch (err) {
    logError('price-config request errored', err);
    return { config: null, domainNotFound: false, domainNotSetup: false, domainDisabled: false };
  }
}

// fetchDonations loads the domain's active donation products so showDonations()
// can mount the launcher. Returns null on any failure so the caller renders
// nothing.
export async function fetchDonations(): Promise<DonationsConfig | null> {
  return (await fetchDonationsDetailed()).config;
}

export async function fetchDonationsDetailed(): Promise<FetchResult<DonationsConfig>> {
  const origin = window.location.origin;
  const base = getApiBase();
  const url = withSlug(`${base}/onsite/donations?origin=${encodeURIComponent(origin)}`);

  try {
    const resp = await fetchWithRefresh(url, { headers: sandboxHeaders() });
    if (!resp.ok) {
      const body = await resp.json().catch(() => null) as ApiResponse<unknown> | null;
      logError('donations request failed', resp.status);
      exitSandboxOnGateRejection(resp, body?.message);
      return configFailure(resp, body);
    }
    const body: ApiResponse<DonationsConfig> = await resp.json();
    if (!body.success) return { config: null, domainNotFound: false, domainNotSetup: false, domainDisabled: false };
    applyVisitorLanguage(body.data.visitorLanguage);
    return { config: body.data, domainNotFound: false, domainNotSetup: false, domainDisabled: false };
  } catch (err) {
    logError('donations request errored', err);
    return { config: null, domainNotFound: false, domainNotSetup: false, domainDisabled: false };
  }
}

// Memoized site-config load. The domain-level config (authMode, domainId) is
// stable for the page, so the first reader pays the round-trip and later reads
// reuse it. A failed load is not cached (the promise is cleared) so a later read
// can retry.
let siteConfigPromise: Promise<FetchResult<SiteConfig>> | null = null;

async function loadSiteConfig(): Promise<FetchResult<SiteConfig>> {
  const origin = window.location.origin;
  const base = getApiBase();
  const url = withSlug(`${base}/onsite/site-config?origin=${encodeURIComponent(origin)}`);

  try {
    const resp = await fetchWithRefresh(url, { headers: sandboxHeaders() });
    if (!resp.ok) {
      const body = await resp.json().catch(() => null) as ApiResponse<unknown> | null;
      logError('site-config request failed', resp.status);
      exitSandboxOnGateRejection(resp, body?.message);
      return configFailure(resp, body);
    }
    const body: ApiResponse<SiteConfig> = await resp.json();
    if (!body.success) return { config: null, domainNotFound: false, domainNotSetup: false, domainDisabled: false };
    applyVisitorLanguage(body.data.visitorLanguage);
    setTestKeysBackend(body.data.sandbox === true);
    return { config: body.data, domainNotFound: false, domainNotSetup: false, domainDisabled: false };
  } catch (err) {
    logError('site-config request errored', err);
    // Config failure leaves the test-keys bit false: no test-mode messaging
    // this page load (fail-closed is the safe direction for live visitors).
    return { config: null, domainNotFound: false, domainNotSetup: false, domainDisabled: false };
  }
}

// getSiteConfigDetailed returns the memoized domain-level config with the
// failure kind attached — login() gates the local-dev guidance on it (a
// tenant 404 on a local origin is a tenant problem; a transport/5xx failure
// is not). A failed load is not cached (the promise is cleared) so a later
// read can retry.
export async function getSiteConfigDetailed(): Promise<FetchResult<SiteConfig>> {
  if (!siteConfigPromise) {
    siteConfigPromise = loadSiteConfig().then((result) => {
      if (!result.config) {
        siteConfigPromise = null;
      }
      return result;
    });
  }
  return siteConfigPromise;
}

// getSiteConfig returns the memoized domain-level config, or null when it can't
// be loaded. Shared by bootstrap (ezAuth restoration needs the domainId) and the
// checkout entry points (auth mode gating + ezAuth token resolution).
export async function getSiteConfig(): Promise<SiteConfig | null> {
  return (await getSiteConfigDetailed()).config;
}

// sendScriptHeartbeat reports that the widget executed on this site — the
// script-install signal behind onboarding step 3 and the "which pages is the
// script on" list. Skipped for named crawlers (isKnownCrawler) except in a
// sandbox preview, which is always the publisher, and throttled to one beacon
// per hour per browser (shouldSendHeartbeat, matching the backend's
// 1h activity debounce window). sendBeacon is CORS-safe (no preflight) and
// survives page unload; fetch keepalive is the fallback. Never throws.
export function sendScriptHeartbeat(now: number = Date.now()): void {
  if (!isSandboxMode() && isKnownCrawler()) {
    return;
  }
  if (!shouldSendHeartbeat(now)) {
    return;
  }
  const base = getApiBase();
  const url = `${base}/onsite/script-heartbeat?origin=${encodeURIComponent(window.location.origin)}&path=${encodeURIComponent(window.location.pathname)}`;
  try {
    // On the sandbox instance every onsite route is gated and sendBeacon can't
    // set headers, so the beacon goes by fetch with the preview token attached. Live
    // keeps the preflight-less sendBeacon path.
    if (isSandboxMode()) {
      void fetch(url, { method: 'POST', keepalive: true, headers: authHeaders() }).catch((err) => logError('script heartbeat failed', err));
      return;
    }
    if (navigator.sendBeacon && navigator.sendBeacon(url)) {
      return;
    }
    void fetch(url, { method: 'POST', keepalive: true }).catch((err) => logError('script heartbeat failed', err));
  } catch (err) {
    logError('script heartbeat failed', err);
  }
}

// Funnel telemetry event names — the stages the publisher-facing funnel
// reports on. checkout_completed is deliberately absent: it is emitted
// server-side on the checkout ledger stamp, never by the widget.
// wallets_ready is a timing marker, not a stage: it fires when the Express
// Checkout Element reports ready, so wallet-surface load latency reads as the
// t_epoch delta from payment_step_shown on the same pageview.
export type FunnelEventName =
  | 'modal_shown'
  | 'modal_dismissed'
  | 'price_selected'
  | 'identity_method_selected'
  | 'payment_step_shown'
  | 'payment_failed'
  | 'wallets_ready'
  // A campaign code (URL ?promo= stash or the SDK promoCode option)
  // successfully auto-applied at checkout — success-only by contract.
  | 'promo_campaign_apply'
  // The same auto-apply failing at the payment step; errorCode carries the
  // machine reason. Diagnostic only — not a funnel stage.
  | 'promo_campaign_apply_failed';

// FunnelSurface/FunnelFlow live in @/types with the other shared wire types;
// re-exported here so existing '@/api/client' importers keep working.
export type { FunnelSurface, FunnelFlow };

// The identity disposition recorded on identity_method_selected: the visitor's
// gate choice (guest email, ezAuth/BYO login, create account, Google or
// Facebook/Apple/Microsoft SSO),
// 'existing_session' when a content session / adapter credential let the flow
// skip the gate with no selection action at all, or 'wallet' when a wallet
// (Apple Pay / Google Pay / Link) supplied identity at confirm on the
// expedited screen — the deferred-identity path has no gate selection.
export type IdentityMethod = 'guest' | 'login' | 'create_account' | 'google' | 'facebook' | 'apple' | 'microsoft' | 'existing_session' | 'wallet';

// Optional context an event carries. The backend drops empty fields, so
// callers pass whatever the stage knows.
export interface FunnelEventContext {
  surface?: FunnelSurface;
  // Which checkout UX the event came from: the classic multi-step flow or the
  // expedited single screen.
  flow?: FunnelFlow;
  // The publisher's price handle (openCheckout({price}) or the plan picker's
  // priceKey). The backend resolves it to the catalog price and stamps
  // amount/currency/interval/trial_days itself, so the client never asserts
  // financials.
  priceKey?: string;
  // The one-time purchase item (openCheckout({price, item}) or
  // showPaywall({item})), resolved for the selected price — set on
  // item-scoped checkouts only. The backend stamps it on the funnel event so
  // the funnel can break down offered vs completed per item.
  item?: string;
  priceExternalId?: string;
  productExternalId?: string;
  amountCents?: number;
  currency?: string;
  billingInterval?: string;
  // Stripe.js error.code on payment_failed; the promo_campaign_apply_failed
  // diagnostic (validate reason / Stripe's apply-rejection text) there; unset
  // for every other event.
  errorCode?: string;
  // Promo code the visitor successfully applied before the event (set on
  // payment_failed); the backend stamps the canonical code on
  // checkout_completed itself.
  promoCode?: string;
  // Set only on identity_method_selected: the visitor's selection at the
  // identity gate. Non-SSO methods are recorded at the selection click;
  // google is recorded at credential resolution (the GIS iframe swallows the
  // click), and facebook/apple/microsoft too (their popup can be cancelled).
  // 'existing_session' is recorded instead when a content session or
  // adapter credential skips the gate entirely — no selection action exists,
  // but without it the authed-skip cohort is an invisible gap between
  // identity and payment_step_shown. 'wallet' is recorded when a wallet
  // confirm supplied identity on the expedited screen, which has no gate
  // selection.
  identityMethod?: IdentityMethod;
  // The tender behind a payment_failed: 'card' on the form path, the wallet's
  // expressPaymentType (apple_pay / google_pay / link) on a wallet confirm.
  // Unset for every other event; checkout_completed's payment_method is
  // derived server-side from the Stripe object instead.
  paymentMethod?: string;
}

// Platform pageview id, when an Ezoic platform script is on the page. The
// fallback chain mirrors staticsol's own widgets (affiliate-widgets.js):
// _ezaq carries the sol-minted id on nameserver-integrated pages; standalone
// (sa.min.js) mints a client id stored on ezstandalone._pageViewId /
// _ezStandalonePageViewId — deliberately never on _ezaq, which sol reads as an
// init-state signal. Absent on SM-only installs; the field stays empty there.
function getPlatformPageViewId(): string | undefined {
  const w = window as unknown as {
    _ezaq?: { page_view_id?: unknown };
    ezstandalone?: { _pageViewId?: unknown };
    _ezStandalonePageViewId?: unknown;
  };
  const id = w._ezaq?.page_view_id ?? w.ezstandalone?._pageViewId ?? w._ezStandalonePageViewId;
  return typeof id === 'string' && id !== '' ? id : undefined;
}

// A pop-on-load modal_shown can outrun the platform script that resolves the
// pageview id: on nameserver pages _ezaq.page_view_id lands only after
// analytics.js round-trips, and standalone mints its id when sa.min.js's init
// runs — either can come after us. Mirror staticsol's own bounded wait
// (init.js's _waitForPageViewId): queue events fired inside the boot window,
// flush as soon as the id appears, and give up after 2s — counts matter even
// when the join key never shows.
const PAGEVIEW_ID_WAIT_INTERVAL_MS = 100;
const PAGEVIEW_ID_MAX_WAIT_MS = 2000;

const bootEpochMs = Date.now();
const pendingEmits: Array<() => void> = [];
let pageviewWaitResolved = false;
let pageviewWaitTimerStarted = false;

function flushPendingEmits(): void {
  pageviewWaitResolved = true;
  window.removeEventListener('pagehide', flushPendingEmits);
  const queued = pendingEmits.splice(0);
  for (const emit of queued) {
    emit();
  }
}

function startPageviewIdWait(): void {
  if (pageviewWaitTimerStarted) {
    return;
  }
  pageviewWaitTimerStarted = true;
  // A setTimeout-delayed beacon dies with the page; flush on the way out so a
  // queued event isn't lost to a fast navigation. pagehide also fires on
  // bfcache entry, so a back-navigated page resumes with the wait resolved and
  // won't re-wait for a late id — accepted under-join on that edge flow;
  // re-arming on pageshow.persisted isn't worth the state complexity.
  window.addEventListener('pagehide', flushPendingEmits);
  const tick = (): void => {
    if (pageviewWaitResolved || getPlatformPageViewId() || Date.now() - bootEpochMs >= PAGEVIEW_ID_MAX_WAIT_MS) {
      flushPendingEmits();
      return;
    }
    setTimeout(tick, PAGEVIEW_ID_WAIT_INTERVAL_MS);
  };
  setTimeout(tick, PAGEVIEW_ID_WAIT_INTERVAL_MS);
}

// emitWhenPageviewReady sends immediately once the wait is settled (id found
// or window elapsed) — which is always the case for user-driven events — and
// otherwise queues until the id resolves or the deadline hits.
function emitWhenPageviewReady(emit: () => void): void {
  if (pageviewWaitResolved || getPlatformPageViewId() || Date.now() - bootEpochMs >= PAGEVIEW_ID_MAX_WAIT_MS) {
    // Drain first (no-op when empty): an id landing between two load-time
    // events must not let the later event leapfrog the queued earlier one.
    flushPendingEmits();
    emit();
    return;
  }
  pendingEmits.push(emit);
  startPageviewIdWait();
}

// sendEventBeacon POSTs one beacon body to /onsite/events. Same transport
// rules as the script heartbeat: sendBeacon on live (survives unload), fetch
// with the preview token attached on the sandbox instance. The Blob is
// text/plain on purpose: application/json is not CORS-safelisted, so a
// spec-compliant browser must preflight the beacon — and a preflighted beacon
// is unreliable (observed dropped in Chromium; Safari is notorious for it).
// text/plain never preflights; the backend ignores content type. Never
// throws: telemetry must never break a checkout UI.
function sendEventBeacon(body: string): void {
  const base = getApiBase();
  const url = withSlug(`${base}/onsite/events?origin=${encodeURIComponent(window.location.origin)}`);
  try {
    if (isSandboxMode()) {
      void fetch(url, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json', ...authHeaders() }, body }).catch(
        (err) => logError('event beacon failed', err),
      );
      return;
    }
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) {
      return;
    }
    void fetch(url, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'text/plain' }, body }).catch((err) =>
      logError('event beacon failed', err),
    );
  } catch (err) {
    logError('event beacon failed', err);
  }
}

// emitFunnelEvent beacons one conversion-funnel event to the backend, which
// stamps and forwards it to the analytics stream. The body keys are the exact
// contract of POST /onsite/events — the backend rejects unknown fields.
export function emitFunnelEvent(event: FunnelEventName, context: FunnelEventContext = {}): void {
  emitWhenPageviewReady(() =>
    sendEventBeacon(
      JSON.stringify({
        event,
        url: window.location.href,
        pageviewId: getPlatformPageViewId(),
        ...context,
      }),
    ),
  );
}

// trackCustomEvent beacons a publisher-defined event (the public
// ezsubscriptions.trackEvent surface) to the custom-event table. eventName is
// free-form publisher input; the backend validates presence and caps lengths
// per the contract.
export function trackCustomEvent(eventName: string, eventVal?: string): void {
  const name = (eventName ?? '').trim();
  if (name === '') {
    logError('trackEvent requires a non-empty event name');
    return;
  }
  emitWhenPageviewReady(() =>
    sendEventBeacon(
      JSON.stringify({
        event: 'custom',
        eventName: name,
        eventVal: eventVal,
        url: window.location.href,
        pageviewId: getPlatformPageViewId(),
      }),
    ),
  );
}

// The backend's deliverability verdict for an address (onsite VerifyEmail).
// Advisory by design — deliverability never blocks a checkout, so there is no
// hard verdict: 'warn' prompts, anything unrecognized maps to 'ok'.
export type EmailVerdict = 'ok' | 'warn';

// verifyEmail is the pre-payment address check: it asks the backend (cache-first
// on the processor's verification cache, MillionVerifier on a miss) whether the
// address can receive email, so a guest can fix a typo before paying. Advisory
// end to end — it never throws and never blocks: any failure (network, timeout,
// 400/429/5xx, malformed body) resolves 'ok' so a verifier outage or rate limit
// never stops a checkout. The 8s abort sits above SM's 7s read-API timeout so
// the browser never cancels a lookup SM would finish and cache; callers bound
// how long they wait (useEmailVerification.verdictWithin), not this fetch.
export async function verifyEmail(email: string): Promise<EmailVerdict> {
  const base = getApiBase();
  try {
    const resp = await fetch(withSlug(`${base}/onsite/verify-email`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...sandboxHeaders() },
      body: JSON.stringify({ email }),
      signal: AbortSignal.timeout(8000),
    });
    if (!resp.ok) {
      return 'ok';
    }
    const body = (await resp.json().catch(() => null)) as ApiResponse<{ verdict: EmailVerdict }> | null;
    if (!body?.success || !body.data) {
      return 'ok';
    }
    return body.data.verdict === 'warn' ? 'warn' : 'ok';
  } catch (err) {
    logError('verify-email request errored', err);
    return 'ok';
  }
}

export async function startCheckout(input: CheckoutRequest): Promise<CheckoutResponse> {
  const base = getApiBase();
  // `session` is a client-side marker (visitor chose session-credential
  // checkout); it is stripped from the body. The bearer goes out for
  // session-credential checkouts, or for any sandbox checkout (the gate
  // requires a token on every route) — an anonymous live guest checkout
  // never carries one, so a stored session can't override the guest email.
  const { session, ...payload } = input;
  payload.pageviewId = getPlatformPageViewId();
  payload.url = window.location.href;
  const { resp, session: request } = await fetchSession(withSlug(`${base}/onsite/checkout`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(session ? authHeaders() : sandboxHeaders()) },
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    // A session-credential 401 means the bearer is dead; the caller re-identifies.
    if (session && resp.status === 401) {
      request.dropIfDead();
    }
    exitSandboxOnGateRejection(resp, body?.message);
    throw new ApiError(body?.message ?? `Checkout failed (${resp.status})`, resp.status);
  }

  const body: ApiResponse<CheckoutResponse> = await resp.json();
  if (!body.success || !body.data) {
    throw new Error(body.error ?? 'Checkout failed');
  }
  return body.data;
}

// startTrial opens a SetupIntent-first trial. Gate 1 never denies here — the
// paywall is optimistic and the denial always lands at confirmTrial, where a
// saved card exists to convert on. Any non-2xx throws.
export async function startTrial(input: TrialStartRequest): Promise<TrialStartResponse> {
  const base = getApiBase();
  const { session, ...payload } = input;
  const { resp, session: request } = await fetchSession(withSlug(`${base}/onsite/trial/start`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(session ? authHeaders() : sandboxHeaders()) },
    body: JSON.stringify(payload),
  });

  const body = await resp.json().catch(() => null) as ApiResponse<TrialStartResponse> | null;
  if (!resp.ok) {
    // Same dead-bearer cleanup as startCheckout.
    if (session && resp.status === 401) {
      request.dropIfDead();
    }
    exitSandboxOnGateRejection(resp, body?.message);
    throw new ApiError(body?.message ?? `Trial start failed (${resp.status})`, resp.status);
  }
  if (!body?.success || !body.data) {
    throw new Error(body?.error ?? body?.message ?? 'Trial start failed');
  }
  return body.data;
}

// confirmTrial creates the trialing subscription on the card captured by the
// SetupIntent. It returns the same completion shape as completeCheckout (an
// ineligibleReason of 'trial_card_used' or 'trial_already_redeemed' is a trial
// gate's soft denial, not an error).
export function confirmTrial(input: TrialFinalizeRequest): Promise<CheckoutCompletionResponse> {
  return finalizeTrial('/onsite/trial/confirm', input);
}

// convertToPaid creates a non-trial subscription on the card already saved to the
// customer (no card re-entry) — the path a reused-card trial denial routes to.
export function convertToPaid(input: TrialFinalizeRequest): Promise<CheckoutCompletionResponse> {
  return finalizeTrial('/onsite/trial/convert-to-paid', input);
}

// finalizeTrial is the shared confirm/convert request: both POST the same body,
// return the completion shape, and store the issued visitor session so later
// /onsite/me/* calls authenticate via Authorization: Bearer.
async function finalizeTrial(path: string, input: TrialFinalizeRequest): Promise<CheckoutCompletionResponse> {
  const base = getApiBase();
  const { session, ...payload } = input;
  payload.pageviewId = getPlatformPageViewId();
  payload.url = window.location.href;
  const { resp, session: request } = await fetchSession(withSlug(`${base}${path}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(session ? authHeaders() : sandboxHeaders()) },
    body: JSON.stringify(payload),
  });

  const body = await resp.json().catch(() => null) as ApiResponse<CheckoutCompletionResponse> | null;
  if (!resp.ok) {
    exitSandboxOnGateRejection(resp, body?.message);
    throw new ApiError(body?.message ?? `Trial finalize failed (${resp.status})`, resp.status);
  }
  if (!body?.success || !body.data) {
    throw new Error(body?.error ?? body?.message ?? 'Trial finalize failed');
  }
  // A stale request (sign-out or a new sign-in since) skips the write but
  // still returns the completion so the checkout UI finishes.
  if (body.data.sessionToken && !keepsVerifiedSession(body.data.sessionToken)) {
    request.adopt(body.data.sessionToken, body.data.sessionExpiresAt ?? '');
  }
  return body.data;
}

// validatePromoCode checks an entered code against the trial's product/price on
// the trial step, which has no Checkout Session to redeem it. It returns the
// backend's verdict so the widget can show the applied chip or a reason; the code
// is only truly attached at confirmTrial. A non-2xx throws so the caller shows a
// generic retry message rather than a false "invalid".
export async function validatePromoCode(input: PromoValidationRequest): Promise<PromoValidationResponse> {
  const base = getApiBase();
  const resp = await fetchWithRefresh(withSlug(`${base}/onsite/promo-codes/validate`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...sandboxHeaders() },
    body: JSON.stringify(input),
  });

  const body = await resp.json().catch(() => null) as ApiResponse<PromoValidationResponse> | null;
  if (!resp.ok) {
    exitSandboxOnGateRejection(resp, body?.message);
    throw new Error(body?.message ?? `Promo code check failed (${resp.status})`);
  }
  if (!body?.success || !body.data) {
    throw new Error(body?.error ?? body?.message ?? 'Promo code check failed');
  }
  return body.data;
}

// completeCheckout's session defaults to one begun at the call; a multi-step
// operation passes the request it began before its first step, so a sign-out
// at any point in the operation keeps the completion from storing a session.
export async function completeCheckout(checkoutId: string, session: SessionRequest = beginSessionRequest()): Promise<CheckoutCompletionResponse> {
  const base = getApiBase();
  const { resp, session: request } = await fetchSession(withSlug(`${base}/onsite/checkout/${encodeURIComponent(checkoutId)}/complete`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...sandboxHeaders() },
  }, session);

  const body = await resp.json().catch(() => null) as ApiResponse<CheckoutCompletionResponse> | null;
  if (!resp.ok) {
    exitSandboxOnGateRejection(resp, body?.message);
    throw new Error(body?.message ?? `Checkout completion failed (${resp.status})`);
  }
  if (!body?.success || !body.data) {
    throw new Error(body?.error ?? body?.message ?? 'Checkout completion failed');
  }
  // The backend issues the visitor session on completion; store it so
  // subsequent /onsite/me/* calls are authenticated via Authorization: Bearer.
  // A stale operation skips the write and still returns the completion.
  if (body.data.sessionToken && !keepsVerifiedSession(body.data.sessionToken)) {
    request.adopt(body.data.sessionToken, body.data.sessionExpiresAt ?? '');
  }
  return body.data;
}

// waitForBackendCompletion polls SM's completion endpoint until the webhook
// (or the synchronous completion path) has provisioned access, returning the
// completion — which carries the adopted email on the deferred-identity
// paths — or null when provisioning outlives the poll.
export async function waitForBackendCompletion(checkoutId: string, session: SessionRequest = beginSessionRequest()): Promise<CheckoutCompletionResponse | null> {
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const completion = await completeCheckout(checkoutId, session);
    if (completion.status === 'completed') {
      return completion;
    }
    if (attempt < 3) {
      await delay(1000);
    }
  }
  return null;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export type SubscriptionConfirmResult =
  | { status: 'completed'; completion: CheckoutCompletionResponse }
  // Confirmed with Stripe, but provisioning outlived the completion poll.
  | { status: 'pending' }
  // Not paid: authentication failed or abandoned, a decline, or another
  // Stripe confirm error. reason is set only for the two buyer-actionable
  // causes; Stripe's message is never carried (it is developer text).
  | { status: 'failed'; reason?: SubscriptionPaymentFailureReason };

export type SubscriptionPaymentFailureReason = 'authentication_failed' | 'card_declined';

function subscriptionFailureReason(error: StripeError): SubscriptionPaymentFailureReason | undefined {
  if (error.code === 'payment_intent_authentication_failure') {
    return 'authentication_failed';
  }
  if (error.type === 'card_error') {
    return 'card_declined';
  }
  return undefined;
}

// confirmSubscriptionPayment runs the on-session authentication a
// direct-created subscription's first invoice needs (ConfirmTrial /
// ConvertToPaid pending with a clientSecret): confirm the invoice's
// PaymentIntent — the saved card is already attached, so the bank's 3DS
// challenge is the only step — then poll the completion endpoint. The pending
// marker is set before confirming so a bank-forced full-page redirect resumes
// via the bootstrap's resumePendingCheckout. confirmCardPayment is modal-first
// (no redirect option exists on it); a redirect happens only when the bank
// requires it. paymentMethodId is the saved card: after a decline or failed
// 3DS the PaymentIntent no longer holds one, and Stripe rejects a confirm
// without it.
export async function confirmSubscriptionPayment(stripe: Stripe, checkoutId: string, clientSecret: string, paymentMethodId?: string): Promise<SubscriptionConfirmResult> {
  // Begun before the 3DS modal: a sign-out during it voids the later session.
  const session = beginSessionRequest();
  setPendingCheckout(checkoutId);
  const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
    return_url: window.location.href,
    ...(paymentMethodId ? { payment_method: paymentMethodId } : {}),
  });
  if (error) {
    clearPendingCheckout();
    logWarn('subscription payment confirm failed', { type: error.type, code: error.code });
    return { status: 'failed', reason: subscriptionFailureReason(error) };
  }
  if (!paymentIntent || (paymentIntent.status !== 'succeeded' && paymentIntent.status !== 'processing')) {
    clearPendingCheckout();
    return { status: 'failed' };
  }
  const completion = await waitForBackendCompletion(checkoutId, session);
  if (!completion) {
    return { status: 'pending' };
  }
  clearPendingCheckout();
  return { status: 'completed', completion };
}

export interface UpgradeSource {
  subscriptionId: string;
  productName: string;
  priceName: string;
  amountCents: number;
  currency: string;
  billingInterval: string;
  billingIntervalCount: number;
  blockedReason?: string;
  // Unix seconds; set while the subscription is in a free trial.
  trialEndsAt?: number;
  inProgress?: { upgradeId: string; status: string };
  targets: { priceExternalId: string; amountCents: number; currency: string }[];
}

export interface UpgradeQuote {
  upgradeId: string;
  currency: string;
  amountDueCents: number;
  newAmountCents: number;
  // Stripe's unused-time credit from the preview's proration lines; 0 on a trial conversion.
  creditCents: number;
  // Customer balance / pending invoice items in the amount due; signed, usually 0.
  adjustmentCents: number;
  // Credit beyond the new charge; Stripe keeps it as customer balance for future invoices.
  carryoverCreditCents?: number;
  billingInterval: string;
  billingIntervalCount: number;
  renewsAt: number;
  presentmentCurrency?: string;
  approxPresentmentAmountDue?: number;
  fromProductName: string;
  fromPriceName: string;
  toProductName: string;
  toPriceName: string;
  // Successful conversion ends the trial; account credits may cover the payment.
  convertsTrial: boolean;
  // The method the upgrade would charge; null when none is saved. Absent from
  // backends without inline payment change, which keep the portal recovery.
  paymentMethod?: UpgradePaymentMethod | null;
  walletPaymentMethods?: ('apple_pay' | 'google_pay')[];
  publishableKey?: string;
  providerAccount?: string;
}

export interface UpgradePaymentMethod {
  type: string;
  walletType?: string;
  brand?: string;
  last4?: string;
}

// A card-only, off-session SetupIntent bound to the upgrading subscription.
export interface UpgradePaymentSetup {
  setupIntentId: string;
  clientSecret: string;
  publishableKey: string;
  providerAccount?: string;
}

export interface UpgradeResult {
  upgradeId: string;
  status: 'applied' | 'requires_action' | 'pending' | 'failed' | 'expired' | 'canceled';
  reasonCode?: string;
  clientSecret?: string;
  publishableKey?: string;
  providerAccount?: string;
  amountChargedCents?: number;
  amountDeferredCents?: number;
  quote?: UpgradeQuote;
  priceExternalId?: string;
  // Client-only: Stripe.js's own message for a failed authentication, shown as
  // normal checkout shows it.
  providerMessage?: string;
}

// Reads (options, status) carry a deadline so a stalled request falls back
// instead of holding the paywall blank; quote/confirm do not, since aborting
// in the browser never stops the server's Stripe call.
async function upgradeRequest<T>(path: string, body?: object, timeoutMs?: number): Promise<T> {
  const response = await fetchWithRefresh(withSlug(`${getApiBase()}/onsite/me/${path}`), {
    method: body ? 'POST' : 'GET',
    headers: { ...authHeaders(), ...(body ? { 'Content-Type': 'application/json' } : {}) },
    cache: 'no-store',
    body: body ? JSON.stringify(body) : undefined,
    signal: timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined,
  });
  const result = await response.json().catch(() => null) as { success: boolean; data?: T; message?: string; code?: string } | null;
  if (!response.ok || !result?.success || !result.data) {
    throw new ApiError(result?.message ?? 'Upgrade unavailable', response.status, result?.code);
  }
  return result.data;
}

export function getUpgradeOptions(productKey: string, priceExternalId?: string): Promise<{ sources: UpgradeSource[]; maskedEmail?: string }> {
  const query = new URLSearchParams({ product: productKey });
  if (priceExternalId) {
    query.set('price', priceExternalId);
  }
  return upgradeRequest(`upgrade-options?${query}`, undefined, 8000);
}

export function createUpgradeQuote(subscriptionId: string, priceExternalId: string): Promise<UpgradeQuote> {
  return upgradeRequest('upgrades/quote', { subscriptionId, priceExternalId });
}

// convertsTrial echoes the quote shown: the server refuses a trial-ending
// confirm without it. setupIntentId names a card just verified inline; the
// server makes it this subscription's default before charging.
export function confirmUpgrade(upgradeId: string, convertsTrial: boolean, setupIntentId?: string, wallet?: { paymentMethodId: string }): Promise<UpgradeResult> {
  if (setupIntentId && wallet) {
    return Promise.reject(new Error('Upgrade confirmation accepts only one payment method'));
  }
  const body: { convertsTrial: boolean; setupIntentId?: string; paymentMethodId?: string } = { convertsTrial };
  if (wallet) {
    body.paymentMethodId = wallet.paymentMethodId;
  }
  if (setupIntentId) {
    body.setupIntentId = setupIntentId;
  }
  return upgradeRequest(`upgrades/${encodeURIComponent(upgradeId)}/confirm`, body);
}

// Creating the setup never charges or changes the subscription's default.
export function createUpgradePaymentSetup(upgradeId: string): Promise<UpgradePaymentSetup> {
  return upgradeRequest(`upgrades/${encodeURIComponent(upgradeId)}/payment-method`, {});
}

export function getUpgradeStatus(upgradeId: string): Promise<UpgradeResult> {
  return upgradeRequest(`upgrades/${encodeURIComponent(upgradeId)}`, undefined, 20000);
}

export function isUpgradeTerminal(status: UpgradeResult['status']): boolean {
  return status === 'applied' || status === 'failed' || status === 'expired' || status === 'canceled';
}

export async function confirmUpgradePayment(stripe: Stripe, upgradeId: string, clientSecret: string): Promise<UpgradeResult> {
  setPendingUpgrade(upgradeId);
  const { error } = await stripe.confirmCardPayment(clientSecret, { return_url: window.location.href });
  if (error) {
    // The server knows why (a failed authentication, an invoice voided by a
    // cancel); a settled outcome replaces Stripe.js's generic error.
    const settled = await getUpgradeStatus(upgradeId);
    if (isUpgradeTerminal(settled.status)) {
      clearPendingUpgrade();
      return settled.status === 'failed' && error.message ? { ...settled, providerMessage: error.message } : settled;
    }
    throw new ApiError(error.message ?? 'Payment authentication failed', 422, error.code);
  }
  let result: UpgradeResult = { upgradeId, status: 'pending' };
  for (let attempt = 0; attempt < 4; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    result = await getUpgradeStatus(upgradeId);
    if (isUpgradeTerminal(result.status)) {
      clearPendingUpgrade();
      return result;
    }
  }
  return result;
}

// requestAccess runs an authenticated /onsite/me/access check with the given
// query (product=... for a product check, item=... for a one-time item). A
// 401/403 drops the session and reports the visitor as anonymous.
const accessInFlight = new Map<string, Promise<AccessResponse>>();

// The client initializes before the SDK exposes publisher event subscriptions.
onAccessChange(() => accessInFlight.clear());

function requestAccess(query: string): Promise<AccessResponse> {
  const url = withSlug(`${getApiBase()}/onsite/me/access?${query}`);
  const headers = authHeaders();
  const key = inFlightKey(url, headers);
  const pending = accessInFlight.get(key);
  if (pending) {
    return pending;
  }
  const promise = loadAccess(url, headers).finally(() => {
    // A pre-event completion must not evict a newer request with the same key.
    if (accessInFlight.get(key) === promise) {
      accessInFlight.delete(key);
    }
  });
  accessInFlight.set(key, promise);
  return promise;
}

async function loadAccess(url: string, headers: Record<string, string>): Promise<AccessResponse> {
  // fetchSession renews before the 401 branch below: the sandbox gate
  // slides the vid=0 gate token via the refresh header even when the /me
  // route then 401s it, and that refresh must reach the gate slot.
  const { resp, session } = await fetchSession(url, {
    method: 'GET',
    headers,
    cache: 'no-store',
  });

  if (resp.status === 401 || resp.status === 403) {
    // The session is missing, expired, or invalid; drop it so the widget
    // treats the visitor as anonymous on subsequent loads. (Sandbox: a vid=0
    // preview session 401s here by design — only the visitor slot is
    // dropped; the gate token lives separately and survives.)
    session.dropIfDead();
    return { decision: 'login_required', reasonCode: 'customer_required' };
  }

  const body = await resp.json().catch(() => null) as ApiResponse<AccessResponse> | null;
  if (!resp.ok) {
    throw new Error(body?.message ?? `Access check failed (${resp.status})`);
  }
  if (!body?.success || !body.data) {
    throw new Error(body?.error ?? body?.message ?? 'Access check failed');
  }
  return body.data;
}

// checkProductAccess asks whether the visitor holds access to a named product.
export function checkProductAccess(product: string): Promise<AccessResponse> {
  return requestAccess(`product=${encodeURIComponent(product)}`);
}

// checkItemAccess asks whether the visitor has bought a one-time item anywhere on
// the domain — the publisher-driven check behind hasPurchased({ item }). The item
// is a domain-global key, matched independent of price/product.
export function checkItemAccess(item: string): Promise<AccessResponse> {
  const params = new URLSearchParams({ item });
  return requestAccess(params.toString());
}

// fetchHeldProducts returns the product handles the visitor currently holds. A
// 401/403 drops the session and reports no handles (anonymous).
export async function fetchHeldProducts(): Promise<string[]> {
  const base = getApiBase();
  const { resp, session } = await fetchSession(withSlug(`${base}/onsite/me/products`), {
    method: 'GET',
    headers: authHeaders(),
    cache: 'no-store',
  });

  if (resp.status === 401 || resp.status === 403) {
    session.dropIfDead();
    return [];
  }

  const body = await resp.json().catch(() => null) as ApiResponse<string[]> | null;
  if (!resp.ok) {
    throw new Error(body?.message ?? `Products fetch failed (${resp.status})`);
  }
  if (!body?.success) {
    throw new Error(body?.error ?? body?.message ?? 'Products fetch failed');
  }
  return body.data ?? [];
}

// AdRemovalMintResult distinguishes a real denial from an inconclusive failure so
// the caller can revoke on the former and fail open on the latter:
//   - minted: the visitor is entitled; use the token.
//   - denied: a 403 — a valid session that holds no active entitlement (or SM is
//     disabled for the domain). Authoritative "no ad-free", so the caller revokes.
//   - unavailable: cannot verify entitlement (401 no session, 5xx, network, or a
//     malformed body). The caller keeps any existing suppression (fail open) so a
//     dropped session or an outage never de-suppresses a paying subscriber.
export type AdRemovalMintResult =
  | { status: 'minted'; token: string }
  | { status: 'denied' }
  | { status: 'unavailable' };

// mintAdRemovalToken asks the backend for a signed ad-removal token, issued only
// when the authenticated visitor holds an active entitlement on the domain. The
// widget writes the returned token into the ez_sm_remove_ads cookie, which Sol
// verifies before suppressing ads. Only a 403 is a definitive denial; every other
// non-success (401, 5xx, network, bad body) is unavailable so the caller fails
// open. Unlike the /me reads it does NOT clear the session on 401/403: a 403 is
// "no entitlement" on an otherwise valid session, and ad removal must never log a
// reader out.
export async function mintAdRemovalToken(): Promise<AdRemovalMintResult> {
  const base = getApiBase();
  try {
    const resp = await fetchWithRefresh(withSlug(`${base}/onsite/ad-removal/token`), {
      method: 'POST',
      headers: authHeaders(),
      cache: 'no-store',
    });
    if (resp.status === 403) {
      return { status: 'denied' };
    }
    if (!resp.ok) {
      return { status: 'unavailable' };
    }
    const body = (await resp.json().catch(() => null)) as ApiResponse<{ token: string }> | null;
    if (!body?.success || !body.data?.token) {
      return { status: 'unavailable' };
    }
    return { status: 'minted', token: body.data.token };
  } catch {
    return { status: 'unavailable' };
  }
}

// fetchPurchases returns the visitor's entitlements (subscriptions + one-time
// item purchases) for "your purchases"/downloads pages. A 401/403 drops the
// session and reports none (anonymous).
export async function fetchPurchases(): Promise<Purchase[]> {
  const base = getApiBase();
  const { resp, session } = await fetchSession(withSlug(`${base}/onsite/me/entitlements`), {
    method: 'GET',
    headers: authHeaders(),
    cache: 'no-store',
  });

  if (resp.status === 401 || resp.status === 403) {
    session.dropIfDead();
    return [];
  }

  const body = await resp.json().catch(() => null) as ApiResponse<Purchase[]> | null;
  if (!resp.ok) {
    throw new Error(body?.message ?? `Purchases fetch failed (${resp.status})`);
  }
  if (!body?.success) {
    throw new Error(body?.error ?? body?.message ?? 'Purchases fetch failed');
  }
  return body.data ?? [];
}

// establishSandboxSession trades the single-use sandbox handoff token
// (delivered in the page URL fragment) for the preview token on the sandbox
// host. It is not a visitor identity, so it goes to the gate slot, which
// clearSession (logout, dead-credential 401s) never wipes. The exchange is
// idempotent by the persisted client nonce, so a re-fire returns the original
// session.
export async function establishSandboxSession(handoff: string, nonce: string): Promise<void> {
  const resp = await fetch(`${getSandboxApiBase()}/onsite/sandbox/session/establish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handoff, nonce }),
  });

  const body = await resp.json().catch(() => null) as ApiResponse<{ jwt: string; expiresAt: string }> | null;
  if (!resp.ok || !body?.success || !body.data?.jwt) {
    throw new Error(body?.message ?? `Sandbox session establish failed (${resp.status})`);
  }
  setSandboxGateToken(body.data.jwt, body.data.expiresAt ?? '');
}

// exchangeMagicToken trades the single-use magic-link token (delivered in the
// page URL fragment) for a session JWT, which it stores in localStorage unless a
// sign-out overtook the exchange. A sandbox link minted inside a preview also
// returns the preview token, stored in the gate slot so the link enters the
// preview in any browser.
export async function exchangeMagicToken(token: string): Promise<VerifyResponse> {
  const base = getApiBase();
  const { resp, session } = await fetchSession(`${base}/auth/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  const body = await resp.json().catch(() => null) as ApiResponse<VerifyResponse> | null;
  if (!resp.ok || !body?.success || !body.data?.jwt) {
    throw new Error(body?.message ?? body?.error ?? `Login verification failed (${resp.status})`);
  }
  session.adopt(body.data.jwt, body.data.expiresAt ?? '');
  if (body.data.preview?.jwt) {
    setSandboxGateToken(body.data.preview.jwt, body.data.preview.expiresAt ?? '');
  }
  return body.data;
}

// establishSession exchanges an identity credential for a content session.
// BYO/guest pass a plaintext { email } from the publisher adapter (or an
// SM-minted { identityToken } when the adapter provides getIdentityToken);
// ezAuth passes an { ezAuthToken } the server resolves to an email via PUA so
// the plaintext email never reaches the page. Stores the session and returns
// true when the credential resolves to a known visitor. Returns false when it
// does not, when the caller's isCurrent() is false, or when a sign-out
// overtook the request (nothing stored; the operation's
// isCurrent() is then false too). The visitor's domain is resolved server-side
// from the browser-set Origin header. Network/HTTP failures throw so the caller
// can decide how to degrade. requireVerified (re-verify paths) replaces a
// session stored at send time only with a verified one: a soft result could be
// another visitor and would gain nothing, so it returns false and stores
// nothing. With no stored session it adopts as usual. sameVisitor (the upgrade
// screen) also refuses a result for a different visitor or domain than the
// stored session, or any result when none is stored, so re-verifying never
// signs in or switches the account mid-upgrade.
export async function establishSession(credential: Credential, isCurrent: () => boolean = () => true, operation?: SessionRequest, options: { requireVerified?: boolean; sameVisitor?: boolean } = {}): Promise<boolean> {
  const base = getApiBase();
  // The stored bearer lets the server keep a verified session verified when the
  // new credential alone would only be soft (no-downgrade). Never the sandbox
  // gate token: it carries no visitor.
  const token = getToken();
  const { resp, session } = await fetchSession(`${base}/auth/session/establish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    // The slug hint rides in the body (not the URL) on the /auth/* routes —
    // their contract is a JSON credential envelope.
    body: JSON.stringify({ ...credential, slug: getChannelSlug() || undefined }),
  }, operation);

  const body = await resp.json().catch(() => null) as ApiResponse<EstablishSessionResponse> | null;
  if (!resp.ok) {
    throw new Error(body?.message ?? `Session establish failed (${resp.status})`);
  }
  if (!body?.success || !body.data) {
    throw new Error(body?.error ?? body?.message ?? 'Session establish failed');
  }
  if (!isCurrent() || !body.data.authenticated || !body.data.jwt) {
    return false;
  }
  if (options.requireVerified && token !== null && !tokenVerified(body.data.jwt)) {
    return false;
  }
  if (options.sameVisitor && (token === null || !sameSessionVisitor(token, body.data.jwt))) {
    return false;
  }
  return session.adopt(body.data.jwt, body.data.expiresAt ?? '');
}

// requestPortalLoginLink asks the backend to mint a one-click portal login URL
// for a signed-in visitor (verified ezAuth token or SM-minted BYO identity
// token), so the "Manage subscription" handoff lands them in the subscriber
// portal already signed in instead of re-authenticating. It returns the URL, or
// null when the visitor is not eligible (soft/unknown identity, per the backend)
// or the request fails — the caller falls back to the bare portal URL.
// Best-effort: it never throws, so opening the portal is never blocked by this.
export async function requestPortalLoginLink(credential: Extract<Credential, { ezAuthToken: string } | { identityToken: string }>): Promise<string | null> {
  const base = getApiBase();
  try {
    const resp = await fetch(`${base}/auth/portal/login-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...credential, slug: getChannelSlug() || undefined }),
    });
    const body = await resp.json().catch(() => null) as ApiResponse<{ url: string }> | null;
    if (!resp.ok || !body?.success) {
      return null;
    }
    return body.data?.url || null;
  } catch (err) {
    logError('portal login-link request errored', err);
    return null;
  }
}

// In-flight dedupe for the magic-link send: a same-tick double submit (double
// Enter, double click) would otherwise send two identical sign-in emails — the
// server-side rate limits are abuse caps, not dedupe. The slot clears when the
// request settles, so a deliberate resend still sends.
let magicLinkInFlight: { key: string; promise: Promise<void> } | null = null;

export function requestMagicLink(input: MagicLinkRequest): Promise<void> {
  const key = `${input.domainId}:${input.email.trim().toLowerCase()}`;
  if (magicLinkInFlight?.key === key) {
    return magicLinkInFlight.promise;
  }
  const promise = postMagicLink(input);
  magicLinkInFlight = { key, promise };
  const clear = () => {
    if (magicLinkInFlight?.promise === promise) {
      magicLinkInFlight = null;
    }
  };
  promise.then(clear, clear);
  return promise;
}

async function postMagicLink(input: MagicLinkRequest): Promise<void> {
  const base = getApiBase();
  // In a preview the preview token rides along so the emailed link carries it.
  const resp = await fetch(`${base}/auth/magic-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...previewHeaders() },
    body: JSON.stringify({ ...input, slug: getChannelSlug() || undefined }),
  });

  const body = await resp.json().catch(() => null) as ApiResponse<{ message: string }> | null;
  if (!resp.ok) {
    throw new Error(body?.message ?? `Login link request failed (${resp.status})`);
  }
  if (!body?.success) {
    throw new Error(body?.error ?? body?.message ?? 'Login link request failed');
  }
}

export function generateIdempotencyKey(): string {
  // crypto.randomUUID is secure-context-only, so it is undefined when a
  // publisher page is reached over plain HTTP. getRandomValues is not gated.
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
